import React, { useContext, useEffect, useState } from 'react'
import {
  Container,
  ElementImage,
  ElementName,
  Section,
  SectionElement,
  SectionElements,
  SectionTitle,
  StyledLink,
} from './style'
import arrow from '../../../assets/home/arrow.svg'
import { TypeMainOutlet } from '..'
import { useOutletContext } from 'react-router-dom'
import UserContext, { Roles } from '../../../contexts/UserContext'
import { useTenant } from '../../../contexts/TenantContext'
import {
  getModulesBySectionWithPermissions,
  getModulesForTenant,
  hasModulePermission,
  ModuleConfig,
} from '../../../config/modulesByTenant'
import SectionContext from '../../../contexts/SectionContext'
import { ListFilter, PlusCircle, SquarePen, Trash2 } from 'lucide-react'
import CreatePipelineModal from '../../../components/Pipelines/Modals/CreatePipelineModal'
import axios from '../../../config/axios'

const HomeScreen: React.FC = () => {
  const { setSearch } = useOutletContext<TypeMainOutlet>()
  const { user } = useContext(UserContext)
  const { tenantId } = useTenant()
  const { permissions } = useContext(SectionContext)
  const [ createPipelineModal, setCreatePipelineModal ] = useState(false)
  const [ pipelines, setPipelines ] = useState<{id: number, name: string}[]>([])

  const handleRenamePipeline = async (pipelineId: number, currentName: string) => {
    const newName = window.prompt('Digite o novo nome do funil', currentName)
    const trimmed = newName?.trim()
    if (!trimmed || trimmed === currentName) return
    try {
      const response = await axios.patch(`${process.env.REACT_APP_GATEWAY_BASE_URL}/pipelines/pipeline/${pipelineId}`, { name: trimmed })
      const updatedName = response.data?.data?.name || trimmed
      setPipelines(prev => prev.map(p => p.id === pipelineId ? { ...p, name: updatedName } : p))
    } catch (error) {
      console.error('Erro ao renomear funil:', error)
    }
  }

  const handleDeletePipeline = async (pipelineId: number, pipelineName: string) => {
    const confirmed = window.confirm(`Deseja excluir o funil "${pipelineName}"?`)
    if (!confirmed) return
    try {
      await axios.delete(`${process.env.REACT_APP_GATEWAY_BASE_URL}/pipelines/pipeline/${pipelineId}`)
      setPipelines(prev => prev.filter(p => p.id !== pipelineId))
    } catch (error) {
      console.error('Erro ao excluir funil:', error)
    }
  }
  
  useEffect(() => {
    setSearch('')
    const fetchPipelines = async () => {
      try {
        const response = await axios.get(`${process.env.REACT_APP_GATEWAY_BASE_URL}/pipelines/pipeline`)
        if(response.data) {
          setPipelines(response.data.data)
        }
      } catch (error) {
        console.warn('⚠️ Serviço de pipelines indisponível:', error)
        setPipelines([])
      }
    }
    fetchPipelines()
  }, [])

  console.log('HomeScreen Debug:', {
    hasUser: !!user,
    userId: user?.id,
    userSub: user?.sub,
    userRole: user?.role,
    userPermissions: user?.permissions,
    sectionPermissions: permissions,
    tenantId,
    hasUserPermissions:
      Array.isArray(user?.permissions) && user.permissions.length > 0,
    hasSectionPermissions: Array.isArray(permissions) && permissions.length > 0,
    finalPermissionsWillBe:
      user?.permissions &&
      Array.isArray(user.permissions) &&
      user.permissions.length > 0
        ? 'token permissions'
        : permissions && Array.isArray(permissions) && permissions.length > 0
        ? 'section permissions'
        : tenantId === 2
        ? 'fashion fallback'
        : 'industrial fallback',
  })

  // Se tenantId for explicitamente null, aguardamos (evita tratar 0 como ausente)
  if (tenantId === null) {
    return <Container>Carregando tenant...</Container>
  }

  // Determinar permissões finais
  let finalPermissions: string[]

  if (
    user?.permissions &&
    Array.isArray(user.permissions) &&
    user.permissions.length > 0
  ) {
    finalPermissions = user.permissions
    console.log('Usando permissões do token:', finalPermissions)
  } else if (
    permissions &&
    Array.isArray(permissions) &&
    permissions.length > 0
  ) {
    finalPermissions = permissions
    console.log('Usando permissões do SectionContext:', finalPermissions)
  } else {
    console.log('Usuário sem permissões definidas')

    // Retornar tela de sem permissões
    return (
      <Container>
        <div
          style={{
            padding: '40px 20px',
            textAlign: 'center',
            color: '#666',
            fontSize: '16px',
          }}
        >
          <h3 style={{ color: '#d32f2f', marginBottom: '16px' }}>
            Acesso Negado
          </h3>
          <p style={{ marginBottom: '8px' }}>
            Usuário sem permissões definidas.
          </p>
          <p style={{ marginBottom: '16px' }}>
            Entre em contato com o administrador do sistema.
          </p>
          <p style={{ fontSize: '14px', color: '#999' }}>
            Usuário: {user?.name || user?.email || 'N/A'} (ID:{' '}
            {user?.sub || 'N/A'})
          </p>
        </div>
      </Container>
    )
  }

  console.log('🎯 Permissões finais carregadas:', finalPermissions)

  const modulesBySection = getModulesBySectionWithPermissions(
    tenantId,
    finalPermissions,
  )

  if (Object.keys(modulesBySection).length === 0) {
    return <Container>Nenhum módulo disponível para suas permissões</Container>
  }

  return (
    <Container>
      {Object.entries(modulesBySection).map(([sectionName, modules]) => (
        <Section key={sectionName}>
          <SectionTitle>{sectionName}</SectionTitle>
          <SectionElements>
            {modules.map((module, index) => (
              <StyledLink key={index} to={`/panel/${module.path}`}>
                <SectionElement>
                  <ElementImage src={module.image} alt='icon' />
                  <ElementName>{module.name}</ElementName>
                  <ElementImage
                    className='arrow'
                    src={arrow}
                    alt='arrow-icon'
                  />
                </SectionElement>
              </StyledLink>
            ))}
          </SectionElements>
        </Section>
      ))}
      <Section>
        <SectionTitle>Funis</SectionTitle>
        <SectionElements>
          <SectionElement
            onClick={() => setCreatePipelineModal(true)}
            style={{ backgroundColor: '#E6F4FB', border: '1px solid #0090D3' }}
          >
            <PlusCircle size={30} color='#0090D3' />
            <ElementName>Funis</ElementName>
            <ElementImage className='arrow' src={arrow} alt='arrow-icon' />
          </SectionElement>
          {pipelines.map((pipeline) => (
            <StyledLink
              key={pipeline.id}
              to={`/panel/pipeline?pipelineId=${pipeline.id}`}
            >
              <SectionElement>
                <div style={{ position: 'absolute', top: '10px', right: '10px', display: 'flex', gap: '8px' }}>
                  <button
                    onClick={(e) => { e.preventDefault(); e.stopPropagation(); handleRenamePipeline(pipeline.id, pipeline.name) }}
                    style={{ background: 'transparent', border: 'none', cursor: 'pointer', padding: 4, display: 'flex', alignItems: 'center' }}
                    title='Renomear funil'
                  >
                    <SquarePen size={18} color='#0090D3' />
                  </button>
                  <button
                    onClick={(e) => { e.preventDefault(); e.stopPropagation(); handleDeletePipeline(pipeline.id, pipeline.name) }}
                    style={{ background: 'transparent', border: 'none', cursor: 'pointer', padding: 4, display: 'flex', alignItems: 'center' }}
                    title='Excluir funil'
                  >
                    <Trash2 size={18} color='#dc3545' />
                  </button>
                </div>
                <ListFilter size={30} color='#0090D3' />
                <ElementName>{pipeline.name}</ElementName>
                <ElementImage className='arrow' src={arrow} alt='arrow-icon' />
              </SectionElement>
            </StyledLink>
          ))}
        </SectionElements>
      </Section>
      {createPipelineModal && (
        <CreatePipelineModal
          closeModal={() => setCreatePipelineModal(false)}
          newPipeline={setPipelines}
        />
      )}
    </Container>
  )
}

export default HomeScreen
